import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import classnames from 'classnames';
import { BILL_STATUS, ORDER_STATUS } from 'src/modules/Order/constants';
import { TABLE_SESSION_TYPE } from 'src/modules/Restaurant/constants';
import { getUrl, getSrcSetUrls } from 'src/utils/image';

import Button from 'src/components/Button';
import Image from 'next/image';

import theme from 'src/styles/components/Order.module.scss';
import OrderAddons from '../OrderAddons';

const OrderTile = React.memo(({
    positions,
    orderNumber,
    context,
    decreaseOrderPositionQuantity,
    increaseOrderPositionQuantity,
    status,
    billStatus,
    fingerprint,
    restaurantId,
    tableSessionType,
    canOrder
}) => {
    const intl = useIntl();

    return (
        <>
            <div className={classnames(theme.status)}>
                {status !== ORDER_STATUS.NEW && (<span>{intl.formatMessage({ id: 'App.order' })} {orderNumber}</span>)}
                {status === ORDER_STATUS.NEW && (<span>{intl.formatMessage({ id: 'App.newOrder' })} </span>)}
                {tableSessionType !== TABLE_SESSION_TYPE.PAYMENT_IN_ADVANCE && billStatus !== BILL_STATUS.PAYMENT_ERROR
                    && billStatus !== BILL_STATUS.ABORTED && (intl.formatMessage({ id: `Order.orderStatus${status}` }))}
                {tableSessionType === TABLE_SESSION_TYPE.PAYMENT_IN_ADVANCE
                    && billStatus === BILL_STATUS.PAYMENT_ERROR && (intl.formatMessage({ id: `Order.paymentError` }))}
                {tableSessionType === TABLE_SESSION_TYPE.PAYMENT_IN_ADVANCE
                    && billStatus === BILL_STATUS.ABORTED && (intl.formatMessage({ id: `Order.aborted` }))}
                {tableSessionType === TABLE_SESSION_TYPE.PAYMENT_IN_ADVANCE
                    && billStatus === BILL_STATUS.CLOSED && (intl.formatMessage({ id: `Order.waitingForPayment` }))}
                {tableSessionType === TABLE_SESSION_TYPE.PAYMENT_IN_ADVANCE && billStatus !== BILL_STATUS.PAYMENT_ERROR
                    && billStatus !== BILL_STATUS.ABORTED
                    && billStatus !== BILL_STATUS.CLOSED && (intl.formatMessage({ id: `Order.orderStatus${status}` }))}
            </div>
            {positions.map((position) => {
                const canChangePosition = status === ORDER_STATUS.NEW && fingerprint === position.clientFingerprint && canOrder;

                return (
                    <div key={position.id} className={theme.position}>
                        <div className={theme.leftColumn}>
                            <div className={classnames(theme.userName, {
                                [theme.isMe]: fingerprint === position.clientFingerprint
                              })}
                            >
                                {fingerprint === position.clientFingerprint && (
                                    <>
                                        {intl.formatMessage({ id: 'App.you' })}{' '}({position.clientName})
                                    </>
                                )}
                                {fingerprint !== position.clientFingerprint && (
                                    position.clientName
                                )}
                            </div>
                            <div className={theme.positionName}>
                                {position.name} ({intl.formatNumber(position.price, { style: 'currency', currency: 'PLN' })})
                            </div>
                            {position.addons && <OrderAddons className={theme.addonsContainer} addons={position.addons} />}
                            {position.comment && (
                            <div className={theme.commentContainer}>
                                <div className={theme.commentName}>
                                    {intl.formatMessage({ id: 'App.commentsToOrder' })}
                                </div>
                                <div className={theme.comment}>
                                    {position.comment}
                                </div>
                            </div>
                                        )}
                        </div>
                        <div className={theme.rightColumn}>
                            {position.imageId && (
                                <div className={classnames(theme.photoContainer, { [theme.circle]: position.isCircleMode })}>
                                    <Image
                                      className={theme.photo}
                                      src={getUrl(restaurantId, position.imageId, 'rt:fill/size:100:100/g:ce/q:85')}
                                      srcset={getSrcSetUrls(restaurantId, position.imageId, 'rt:fill/size:100:100/g:ce/q:85')}
                                      alt={position.name}
                                      width={100}
                                      height={100}
                                      unoptimized="true"
                                    />
                                </div>
                            )}
                            <div className={classnames(theme.positionQuantity, {
                                [theme.readonlyPositionQuantity]: !canChangePosition
                              })}
                            >
                                {(canChangePosition) && (
                                    <Button
                                      floating
                                      mini
                                      inverse
                                      icon="icon-minus"
                                      onClick={() => { decreaseOrderPositionQuantity(position, context, positions.length === 1); }}
                                      iconSmall
                                    />
                                )}
                                <div className={theme.quantity}>
                                    {position.quantity}
                                    {' '}
                                    {(!canChangePosition) && intl.formatMessage({ id: 'App.pcs' })}
                                </div>
                                {(canChangePosition) && (
                                    <Button
                                      floating
                                      mini
                                      inverse
                                      icon="icon-plus"
                                      onClick={() => { increaseOrderPositionQuantity(position, context); }}
                                      iconSmall
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}
        </>
    );
});

OrderTile.propTypes = {
    orderNumber: PropTypes.number,
    positions: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        quantity: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        price: PropTypes.number.isRequired,
        imageId: PropTypes.string,
        comment: PropTypes.string
    })),
    decreaseOrderPositionQuantity: PropTypes.func.isRequired,
    increaseOrderPositionQuantity: PropTypes.func.isRequired,
    context: PropTypes.object.isRequired,
    status: PropTypes.number.isRequired,
    billStatus: PropTypes.number,
    fingerprint: PropTypes.string,
    restaurantId: PropTypes.string.isRequired,
    canOrder: PropTypes.bool,
    tableSessionType: PropTypes.number.isRequired
};

export default OrderTile;
